import { c as _c } from "react/compiler-runtime";
import { useCallback, useState } from 'react';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import { ViewConfiguration } from '../../BalanceSheetTable/BalanceSheetTableContext';
import { AppliedSuggestion, BalanceSheetSuggestionPreset } from '../BalanceSheetConfigPanel.suggestions';
interface UseBalanceSheetConfigSuggestionsArgs {
  localConfig: ViewConfiguration | null;
  applyConfig: (config: ViewConfiguration) => Promise<unknown>;
  trackUserEvent: ReturnType<typeof useTrackUserEvent>;
}

/**
 * Manages the chat-style suggestion feature for the balance sheet config panel.
 *
 * Responsibilities:
 * - Config history stack for undo support
 * - Applied suggestions list for the chat-style UI
 * - Thinking indicator driven by the mutation lifecycle
 * - Apply preset / undo handlers
 */
export function useBalanceSheetConfigSuggestions(t0) {
  const $ = _c(18);
  const {
    localConfig,
    applyConfig,
    trackUserEvent
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = [];
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const [configHistory, setConfigHistory] = useState(t1);
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const [appliedSuggestions, setAppliedSuggestions] = useState(t2);
  const [isSuggestionThinking, setIsSuggestionThinking] = useState(false);
  let t3;
  if ($[2] !== applyConfig || $[3] !== localConfig || $[4] !== trackUserEvent) {
    t3 = preset => {
      if (!localConfig) {
        return;
      }
      setConfigHistory(prev => [...prev, localConfig]);
      setAppliedSuggestions(prev_0 => [...prev_0, {
        preset
      }]);
      const updated = {
        ...localConfig,
        ...preset.config
      };
      setIsSuggestionThinking(true);
      applyConfig(updated).finally(() => setIsSuggestionThinking(false));
      trackUserEvent("balance_sheet suggestion applied", {
        suggestionId: preset.id
      });
    };
    $[2] = applyConfig;
    $[3] = localConfig;
    $[4] = trackUserEvent;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  const applyConfigPreset = t3;
  let t4;
  if ($[6] !== applyConfig || $[7] !== configHistory || $[8] !== localConfig || $[9] !== trackUserEvent) {
    t4 = () => {
      if (configHistory.length === 0 || !localConfig) {
        return;
      }
      const previousConfig = configHistory[configHistory.length - 1];
      setConfigHistory(_temp);
      setAppliedSuggestions(_temp2);
      setIsSuggestionThinking(true);
      applyConfig(previousConfig).finally(() => setIsSuggestionThinking(false));
      trackUserEvent("balance_sheet suggestion undone", {});
    };
    $[6] = applyConfig;
    $[7] = configHistory;
    $[8] = localConfig;
    $[9] = trackUserEvent;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  const undoLastChange = t4;
  let t5;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      setConfigHistory([]);
      setAppliedSuggestions([]);
      setIsSuggestionThinking(false);
    };
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const resetSuggestions = t5;
  let t6;
  if ($[12] !== appliedSuggestions || $[13] !== applyConfigPreset || $[14] !== configHistory.length || $[15] !== isSuggestionThinking || $[16] !== undoLastChange) {
    t6 = {
      applyConfigPreset,
      undoLastChange,
      configHistoryLength: configHistory.length,
      appliedSuggestions,
      isSuggestionThinking,
      resetSuggestions
    };
    $[12] = appliedSuggestions;
    $[13] = applyConfigPreset;
    $[14] = configHistory.length;
    $[15] = isSuggestionThinking;
    $[16] = undoLastChange;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  return t6;
}
function _temp2(prev_2) {
  return [...prev_2, {
    preset: {
      id: "undo",
      label: "Undo previous change",
      config: {} as BalanceSheetSuggestionPreset['config'],
      responseMessage: "The previous change has been undone."
    }
  }];
}
function _temp(prev_1) {
  return prev_1.slice(0, -1);
}